/**
 * Teacher Game Monitor Component
 * Real-time dashboard for teachers to monitor game progress
 */

import React, { useState } from 'react';
import { GameState } from '../models/GameState';
import { stageManager, scoringSystem, questionHandler } from '../engine';
import EnergyDisplay from './EnergyDisplay';
import StageView from './StageView';
import { DecisionImpactView } from './DecisionImpactView';
import { ScoreDeltaGroup } from './ScoreDeltaBadge';
import { getPlayerIcon } from '@/config/playerIcons';
import { IoCheckmark, IoHourglass, IoSkull } from 'react-icons/io5';
import { GiPoisonBottle } from 'react-icons/gi';
import {
  getScoreDelta,
  getQuestionText,
  getAnswerText
} from '../utils/scoreDeltaCalculator';

interface TeacherGameMonitorProps {
  gameState: GameState;
  onAdvanceStage?: () => void;
  className?: string;
}

export const TeacherGameMonitor: React.FC<TeacherGameMonitorProps> = ({
  gameState,
  onAdvanceStage,
  className = ''
}) => {
  const [selectedPlayer, setSelectedPlayer] = useState<string | null>(null);
  const [view, setView] = useState<'overview' | 'players' | 'decisions' | 'scores'>('overview');
  const [scoresSubView, setScoresSubView] = useState<'summary' | 'rankings' | 'impact'>('summary');

  const stageProgress = stageManager.getStageProgress(gameState);
  const gameProgress = stageManager.getGameProgress(gameState);

  // Calculate statistics
  const playersAlive = gameState.players.filter(p => p.isAlive).length;
  const averageEnergy = Math.round(
    gameState.players.reduce((sum, p) => sum + p.energy, 0) / gameState.players.length
  );

  const teamScoreSummary = scoringSystem.generateTeamSummary(gameState.players);

  // Get current questions
  const currentQuestions = stageManager.getCurrentQuestions(gameState);

  // Check who has answered
  const answerStatus = currentQuestions.map(qId => ({
    questionId: qId,
    ...questionHandler.getDecisionStatus(qId, gameState)
  }));

  const allQuestionsComplete = answerStatus.every(s => s.complete);

  const renderOverview = () => (
    <div className="space-y-6">
      {/* Game Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm p-6">
          <div className="text-sm text-slate-600 font-medium mb-2">Overall Progress</div>
          <div className="text-3xl font-bold text-[#12356B]">{gameProgress}%</div>
        </div>

        <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm p-6">
          <div className="text-sm text-slate-600 font-medium mb-2">Players Alive</div>
          <div className="text-3xl font-bold text-[#367A25]">
            {playersAlive}/{gameState.players.length}
          </div>
        </div>

        <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm p-6">
          <div className="text-sm text-slate-600 font-medium mb-2">Average Energy</div>
          <div className="text-3xl font-bold text-[#DD6832]">{averageEnergy}%</div>
        </div>

        <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm p-6">
          <div className="text-sm text-slate-600 font-medium mb-2">Energy Packs</div>
          <div className="text-3xl font-bold text-[#12356B]">{gameState.teamEnergyPacks}</div>
        </div>
      </div>

      {/* Stage View */}
      <StageView
        stage={gameState.currentStage}
        huntingPermitDay={gameState.huntingPermitDay}
        progress={stageProgress}
        variant={gameState.currentVariant}
      />

      {/* Current Questions Status */}
      {currentQuestions.length > 0 && (
        <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-bold text-[#12356B] mb-4">Question Progress</h3>
          <div className="space-y-3">
            {answerStatus.map(status => (
              <div key={status.questionId} className="flex items-center justify-between py-2 border-b border-[#E2E5EC] last:border-b-0">
                <span className="font-medium text-[#12356B]">{status.questionId}</span>
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-slate-600">
                    {status.answered}/{status.total} answered
                  </span>
                  {status.complete ? (
                    <span className="text-[#367A25] text-xl"><IoCheckmark /></span>
                  ) : (
                    <span className="text-[#DD6832] text-xl"><IoHourglass /></span>
                  )}
                </div>
              </div>
            ))}
          </div>

          {allQuestionsComplete && onAdvanceStage && (
            <button
              onClick={onAdvanceStage}
              className="mt-4 w-full px-6 py-3 bg-[#367A25] text-white rounded-lg font-semibold hover:bg-[#2d6820] transition-colors"
            >
              Advance to Next Stage
            </button>
          )}
        </div>
      )}
    </div>
  );

  const renderPlayers = () => (
    <div className="space-y-4">
      {gameState.players.map(player => (
        <div
          key={player.playerId}
          className="bg-white border border-[#CED4DE] rounded-xl shadow-sm p-6 cursor-pointer hover:border-[#12356B]/40 hover:shadow-md transition-all"
          onClick={() => setSelectedPlayer(player.playerId === selectedPlayer ? null : player.playerId)}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center flex-wrap gap-3">
                {getPlayerIcon(player.role) && (
                  <img
                    src={getPlayerIcon(player.role)!}
                    alt={`${player.role} helmet`}
                    className="w-10 h-10 object-contain"
                  />
                )}
                <h4 className="text-lg font-bold text-[#12356B]">{player.name}</h4>
                <span className="px-3 py-1 bg-[#12356B]/10 text-[#12356B] text-xs font-semibold rounded-lg">
                  {player.role}
                </span>
                {!player.isAlive && (
                  <span className="px-3 py-1 bg-[#DD6832]/12 text-[#c4562a] text-xs font-semibold rounded-lg flex items-center gap-1">
                    <IoSkull /> Deceased
                  </span>
                )}
                {player.isPoisoned && (
                  <span className="px-3 py-1 bg-[#12356B]/10 text-[#12356B] text-xs font-semibold rounded-lg flex items-center gap-1">
                    <GiPoisonBottle /> Poisoned
                  </span>
                )}
              </div>

              <div className="mt-3">
                <EnergyDisplay
                  energy={player.energy}
                  showLabel={false}
                  showStatus
                  size="small"
                />
              </div>

              {selectedPlayer === player.playerId && (
                <div className="mt-4 pt-4 border-t border-[#CED4DE] space-y-3">
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="text-slate-600">Decisions Made:</span>
                      <span className="ml-2 font-semibold text-[#12356B]">{player.decisions.size}</span>
                    </div>
                    <div>
                      <span className="text-slate-600">Total Score:</span>
                      <span className="ml-2 font-semibold text-[#12356B]">
                        {scoringSystem.calculateTotalScore(player.scores)}
                      </span>
                    </div>
                  </div>
                  <div className="text-sm">
                    <span className="text-slate-600 block mb-2">Scores:</span>
                    <div className="grid grid-cols-4 gap-2">
                      <div className="text-[#12356B]">Brain: <strong>{player.scores.brain}</strong></div>
                      <div className="text-[#12356B]">Heart: <strong>{player.scores.heart}</strong></div>
                      <div className="text-[#12356B]">Task: <strong>{player.scores.task}</strong></div>
                      <div className="text-[#12356B]">Comm: <strong>{player.scores.communication}</strong></div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );

  const renderDecisions = () => (
    <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm p-6">
      <h3 className="text-lg font-bold text-[#12356B] mb-4">Team Decision History</h3>
      {gameState.decisions.length === 0 ? (
        <div className="text-sm text-slate-500 italic">No team decisions recorded yet</div>
      ) : (
        <div className="space-y-4">
          {gameState.decisions.map((decision, idx) => {
            const questionText = getQuestionText(decision.questionId);
            const answerText = getAnswerText(decision.questionId, String(decision.finalAnswer));
            const scoreDelta = getScoreDelta(decision.questionId, String(decision.finalAnswer));

            return (
              <div key={idx} className="border-l-4 border-[#12356B] pl-4 py-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-xs font-semibold text-slate-500 bg-[#E2E5EC] px-2 py-0.5 rounded">
                    {decision.questionId}
                  </span>
                  <span className="text-xs text-slate-500">
                    {new Date(decision.timestamp).toLocaleTimeString()}
                  </span>
                </div>
                <div className="text-sm text-slate-700 mb-2 line-clamp-2">
                  {questionText}
                </div>
                <div className="text-sm mb-2">
                  <span className="text-slate-500">Team Answer: </span>
                  <strong className="text-[#12356B]">{decision.finalAnswer}</strong>
                  {answerText !== String(decision.finalAnswer) && (
                    <span className="text-slate-600"> - {answerText}</span>
                  )}
                </div>
                {scoreDelta && (
                  <ScoreDeltaGroup deltas={scoreDelta} />
                )}
                {/* Show individual player choices */}
                {decision.playerDecisions && decision.playerDecisions.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-[#E2E5EC]">
                    <div className="text-xs text-slate-500 mb-2">Individual Choices:</div>
                    <div className="flex flex-wrap gap-2">
                      {decision.playerDecisions.map((pd) => {
                        const player = gameState.players.find(p => p.playerId === pd.playerId);
                        return (
                          <span
                            key={pd.playerId}
                            className="inline-flex items-center gap-1 px-2 py-1 bg-[#E2E5EC]/50 border border-[#CED4DE] rounded text-xs"
                          >
                            <span className="font-medium text-[#12356B]">{player?.name || pd.playerId}:</span>
                            <span className="text-slate-700">{String(pd.answer)}</span>
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderScoresSubNav = () => (
    <div className="flex gap-2 mb-6">
      <button
        onClick={() => setScoresSubView('summary')}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
          scoresSubView === 'summary'
            ? 'bg-[#12356B] text-white'
            : 'bg-[#E2E5EC] text-[#12356B] hover:bg-[#CED4DE]'
        }`}
      >
        Team Summary
      </button>
      <button
        onClick={() => setScoresSubView('rankings')}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
          scoresSubView === 'rankings'
            ? 'bg-[#12356B] text-white'
            : 'bg-[#E2E5EC] text-[#12356B] hover:bg-[#CED4DE]'
        }`}
      >
        Player Rankings
      </button>
      <button
        onClick={() => setScoresSubView('impact')}
        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
          scoresSubView === 'impact'
            ? 'bg-[#12356B] text-white'
            : 'bg-[#E2E5EC] text-[#12356B] hover:bg-[#CED4DE]'
        }`}
      >
        Decision Impact
      </button>
    </div>
  );

  const renderTeamSummary = () => (
    <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm p-6">
      <h3 className="text-lg font-bold text-[#12356B] mb-6">Team Performance</h3>
      <div className="grid grid-cols-4 gap-6">
        <div className="text-center">
          <div className="text-4xl font-bold text-[#12356B] mb-2">
            {teamScoreSummary.teamScores.brain}
          </div>
          <div className="text-sm text-slate-600 font-medium">Brain 🧠</div>
        </div>
        <div className="text-center">
          <div className="text-4xl font-bold text-[#DD6832] mb-2">
            {teamScoreSummary.teamScores.heart}
          </div>
          <div className="text-sm text-slate-600 font-medium">Heart ❤️</div>
        </div>
        <div className="text-center">
          <div className="text-4xl font-bold text-[#367A25] mb-2">
            {teamScoreSummary.teamScores.task}
          </div>
          <div className="text-sm text-slate-600 font-medium">Task 📋</div>
        </div>
        <div className="text-center">
          <div className="text-4xl font-bold text-[#101822] mb-2">
            {teamScoreSummary.teamScores.communication}
          </div>
          <div className="text-sm text-slate-600 font-medium">Communication 💬</div>
        </div>
      </div>
      <div className="mt-6 pt-6 border-t border-[#CED4DE] text-center">
        <div className="text-3xl font-bold text-[#12356B] mb-1">{teamScoreSummary.teamTotal}</div>
        <div className="text-sm text-slate-600 font-medium">{teamScoreSummary.teamCategory}</div>
      </div>
    </div>
  );

  const renderPlayerRankings = () => (
    <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm p-6">
      <h3 className="text-lg font-bold text-[#12356B] mb-4">Player Rankings</h3>
      <div className="space-y-3">
        {teamScoreSummary.rankings.map((player, idx) => (
          <div key={player.playerId} className="flex items-center justify-between p-4 bg-[#E2E5EC]/50 border border-[#CED4DE] rounded-lg">
            <div className="flex items-center space-x-4">
              <div className="text-2xl font-bold text-slate-500">#{idx + 1}</div>
              {getPlayerIcon(player.role) && (
                <img
                  src={getPlayerIcon(player.role)!}
                  alt={`${player.role} helmet`}
                  className="w-10 h-10 object-contain"
                />
              )}
              <div>
                <div className="font-semibold text-[#12356B]">{player.name}</div>
                <div className="text-sm text-slate-600">{player.role}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-[#12356B]">
                {scoringSystem.calculateTotalScore(player.scores)}
              </div>
              <div className="text-xs text-slate-500">
                B:{player.scores.brain} H:{player.scores.heart} T:{player.scores.task} C:{player.scores.communication}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  const renderScores = () => (
    <div className="space-y-6">
      {renderScoresSubNav()}
      {scoresSubView === 'summary' && renderTeamSummary()}
      {scoresSubView === 'rankings' && renderPlayerRankings()}
      {scoresSubView === 'impact' && <DecisionImpactView gameState={gameState} />}
    </div>
  );

  return (
    <div className={className}>
      {/* Tab Navigation */}
      <div className="bg-white border border-[#CED4DE] rounded-xl shadow-sm mb-6">
        <div className="flex border-b border-[#CED4DE]">
          <button
            onClick={() => setView('overview')}
            className={`px-6 py-3 font-semibold transition-colors ${
              view === 'overview'
                ? 'text-[#12356B] border-b-2 border-[#12356B]'
                : 'text-slate-600 hover:text-[#12356B]'
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setView('players')}
            className={`px-6 py-3 font-semibold transition-colors ${
              view === 'players'
                ? 'text-[#12356B] border-b-2 border-[#12356B]'
                : 'text-slate-600 hover:text-[#12356B]'
            }`}
          >
            Players
          </button>
          <button
            onClick={() => setView('decisions')}
            className={`px-6 py-3 font-semibold transition-colors ${
              view === 'decisions'
                ? 'text-[#12356B] border-b-2 border-[#12356B]'
                : 'text-slate-600 hover:text-[#12356B]'
            }`}
          >
            Decisions
          </button>
          <button
            onClick={() => setView('scores')}
            className={`px-6 py-3 font-semibold transition-colors ${
              view === 'scores'
                ? 'text-[#12356B] border-b-2 border-[#12356B]'
                : 'text-slate-600 hover:text-[#12356B]'
            }`}
          >
            Scores
          </button>
        </div>
      </div>

      {/* Content Area */}
      <div>
        {view === 'overview' && renderOverview()}
        {view === 'players' && renderPlayers()}
        {view === 'decisions' && renderDecisions()}
        {view === 'scores' && renderScores()}
      </div>
    </div>
  );
};

export default TeacherGameMonitor;
